// Hand-rolled types matching the JSON shape produced by
// CrmWork::TasksController#serialize_task / serialize_comments
// (gems/crm_work/app/controllers/crm_work/tasks_controller.rb).
//
// Not generated via Typelizer because the controller serializes inline
// rather than through Alba — keep these in sync manually until the
// engine adopts Alba serializers.

export type TaskStatus =
  | 'pending'
  | 'in_progress'
  | 'on_hold'
  | 'completed'
  | 'cancelled'
  | 'archived'

export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent'

export type TaskType =
  | 'general'
  | 'follow_up'
  | 'call'
  | 'meeting'
  | 'email'
  | 'visit'
  | 'administrative'
  | 'other'

export interface TaskSubject {
  id: number
  type: string
  label: string
  sublabel?: string | null
}

export interface TaskPolicyCaps {
  update: boolean
  update_status: boolean
  update_priority: boolean
  reassign: boolean
  destroy: boolean
  comment: boolean
}

export interface TaskRow {
  id: number
  title: string
  status: TaskStatus
  priority: TaskPriority
  task_type: TaskType
  due_at: string | null
  assignee_user_id: number | null
  assignee_name: string | null
  team_id: number
  team_name: string | null
  created_by_id: number
  created_by_name: string | null
  parent_task_id: number | null
  subject: TaskSubject | null
  subtask_count: number
  comment_count: number
}

export interface TaskDetail extends TaskRow {
  description: string | null
  created_at: string
  policy_caps: TaskPolicyCaps
}

export interface TaskComment {
  id: number
  body: string | null
  author_id: number
  author_name: string | null
  created_at: string
  edited_at: string | null
  can_update: boolean
  can_destroy: boolean
}

export interface AssigneeOption {
  id: number
  label: string
  team_id: number
}

export interface PagyMeta {
  count: number
  page: number
  items: number
  pages: number
  last: number
}

export type TaskDisplay = 'kanban' | 'list'

// US16: orthogonal "group by team" toggle. `team` is the only grouping
// dimension; `null` means ungrouped (the default flat behavior). Server
// allowlist `(team)`, echoed back in the index filter state.
export type TaskGroupBy = 'team' | null

// US14: sortable column keys — must match the controller's SORT_KEYS
// allowlist (gems/crm_work/app/controllers/crm_work/tasks_controller.rb).
// Lié à is deliberately absent: polymorphic link, no meaningful order.
export type TaskSortKey =
  | 'title'
  | 'status'
  | 'priority'
  | 'type'
  | 'due_at'
  | 'assignee'
  | 'team'
  | 'created_by'

export type TaskSortDir = 'asc' | 'desc'

// US16: hierarchy-ordered team-group metadata, emitted by the controller
// only when grouping is active. `depth` is the nesting level within the
// present set (0 for roots) for indentation; `count` is the TRUE per-team
// task count (not the capped/rendered count) so the grouped table can
// render a "voir N de plus" disclosure.
export interface TeamGroup {
  id: number
  name: string
  depth: number
  count: number
}

export interface TaskIndexFilters {
  view: 'mine' | 'team' | null
  status: TaskStatus[]
  priority: TaskPriority[]
  type: TaskType[]
  assignee_id: number[]
  team_id: number[]
  subject_type: string | null
  subject_id: string | null
  due: 'overdue' | 'today' | 'this_week' | null
  q: string | null
  // Sprint 5b U2: routes the page between the Kanban board and the
  // existing list view. Server allowlist `(kanban|list)`, defaults to
  // `kanban` when absent or invalid. Mobile (≤768px) forces 'list' on
  // the client regardless of this prop (Sprint 5b U9).
  display: TaskDisplay
  // US16: orthogonal grouping toggle. `null` when ungrouped (default).
  group_by: TaskGroupBy
  // US14: the RESOLVED sort echoed by the server — null when no valid
  // sort was requested (default due-date order applies). Reading the
  // active sort from here (not the URL) means the header indicator can
  // never show a sort the server refused.
  sort: TaskSortKey | null
  dir: TaskSortDir | null
}

// French display labels for enum values. Keep in sync with
// gems/crm_work/config/locales/crm_work.fr.yml — server is authoritative,
// frontend mirrors for fast rendering without prop drilling labels.
export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  pending: 'À faire',
  in_progress: 'En cours',
  on_hold: 'En attente',
  completed: 'Terminé',
  cancelled: 'Annulé',
  archived: 'Archivé',
}

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Faible',
  medium: 'Moyen',
  high: 'Élevé',
  urgent: 'Urgent',
}

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  general: 'Général',
  follow_up: 'Suivi',
  call: 'Appel',
  meeting: 'Rendez-vous',
  email: 'E-mail',
  visit: 'Visite',
  administrative: 'Administratif',
  other: 'Autre',
}

export const TASK_DUE_FILTER_LABELS = {
  overdue: 'En retard',
  today: 'Aujourd’hui',
  this_week: 'Cette semaine',
} as const

// ------------------------------------------------------------------ //
// US17 — team calendar. Shapes mirror                                  //
// CrmWork::CalendarsController#serialize_calendar_tasks / filter_state //
// ------------------------------------------------------------------ //

export type CalendarView = 'month' | 'week' | 'day'

// Calendar entries only ever carry the four visible statuses — cancelled
// and archived are pinned out server-side (CALENDAR_STATUSES).
export interface CalendarTask {
  id: number
  title: string
  status: TaskStatus
  priority: TaskPriority
  task_type: TaskType
  due_at: string
  assignee_user_id: number | null
  assignee_name: string | null
  team_id: number
  // Server-computed projection of TaskPolicy#update_due_at? — gates the
  // drag affordance client-side; the PATCH endpoint stays authoritative.
  can_reschedule: boolean
}

// Hierarchy-ordered perimeter team option; `depth` drives indentation in
// the picker (same shape as TeamGroup minus the count).
export interface CalendarTeamOption {
  id: number
  name: string
  depth: number
}

export interface CalendarMemberOption {
  id: number
  label: string
}

// `assignee_id` echoes the applied selection as strings — member ids plus
// the literal 'none' ("Non assigné"). `start`/`end` echo the validated
// query window so the page can tell whether the current FullCalendar
// window is already in sync (mount guard for the datesSet → URL loop).
export interface CalendarFilters {
  view: CalendarView
  date: string
  team_id: number | null
  assignee_id: string[]
  start: string | null
  end: string | null
}
